import { getCountyInsights } from "@/lib/county-insights.functions";
import { getState as getStateForCounty } from "@/data/states";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getCityHero } from "@/data/city-heroes";
import { findCity } from "@/data/investor-cities";
import { getMarketStats } from "@/lib/market-stats.functions";
import { BridgeCityPage } from "@/components/site/BridgeCityPage";
import { cityFaqs, bridgeCityMetaDescription } from "@/components/site/BridgeCityPage.seo";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { buildHead } from "@/lib/seo-head";
import { cityKeywordsMeta } from "@/lib/page-keywords";

const SLUG = "bridge-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/bridge-loans/$state/$city")({
  loader: async ({ params }) => {
    const city = findCity(params.state, params.city);
    if (!city) throw notFound();
    const countyInsights = await getCountyInsights({
      data: {
        stateAbbrev: getStateForCounty(params.state)?.abbrev ?? "",
        countyName: city.county,
        placeLabel: `${city.name}, ${city.stateName}`,
      },
    }).catch(() => null);
    const liveStats = await getMarketStats({
      data: { stateSlug: params.state, citySlug: params.city },
    }).catch(() => null);
    return { city, liveStats , countyInsights };
  },
  head: ({ params, loaderData }) => {
    const city = loaderData?.city;
    if (!city) return { meta: [{ title: "City not found" }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}/${params.city}`;
    const hero = getCityHero(params.state, params.city, city.name, city.stateName) ?? HERO;
    const title = `${city.name}, ${city.stateName} Bridge Loans | 80% LTV · 10-Day Close | Simply Approved`;
    const description = bridgeCityMetaDescription(city.name, city.stateName);
    return buildHead({
      title,
      description,
      url,
      keywords: cityKeywordsMeta("bridge-loans", city.name, city.stateName),
      image: hero.src,
      imageAlt: hero.alt,
      type: "article",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: `/${SLUG}` },
        { name: city.stateName, url: `/${SLUG}/${params.state}` },
        { name: city.name, url },
      ],
      faqs: cityFaqs(city.name, city.stateName),
      service: { name: `Bridge Loans in ${city.name}, ${city.stateName}`, state: city.stateName, city: city.name },
      extraJsonLd: [
        productHowToJsonLd(PRODUCT, url),
        productArticleJsonLd(PRODUCT, url),
      ],
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { city, liveStats, countyInsights } = Route.useLoaderData();
  return (
    <BridgeCityPage
      product={PRODUCT}
      city={city}
      liveStats={liveStats}
      countyInsights={countyInsights}
      midSlot={<ProductReportCtaBanner productKey="bridge" variant="full" locationLabel={`${city.name}, ${city.stateName}`} />}
    />
  );
}
